/**
 * PostedCard Component
 *
 * Displays a posted article with social media links.
 * View-only card without approve/decline actions.
 * Enhanced with visual hierarchy and platform indicators.
 */

import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";

// Card styles
import { cardStyles } from "./cardStyles";

// Helper function to format date with relative time
const formatDate = (dateString) => {
  if (!dateString) return "Unknown date";
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

// Platform colors and icons
const platformConfig = {
  blog: { icon: "article", color: "#1a1a2e", bg: "#F3F4F6", label: "Blog" },
  linkedin: { icon: "work", color: "#0A66C2", bg: "#E8F4FD", label: "LinkedIn" },
  x: { icon: "tag", color: "#000000", bg: "#F5F5F5", label: "X" },
  threads: { icon: "alternate_email", color: "#000000", bg: "#F5F5F5", label: "Threads" },
};

function PostedCard({ post, onClick }) {
  const hasSocialLinks =
    post.linkedin_post_url || post.x_post_url || post.threads_post_url || post.blog_url;

  // Count published platforms
  const platformCount = [
    post.blog_url,
    post.linkedin_post_url,
    post.x_post_url,
    post.threads_post_url,
  ].filter(Boolean).length;

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: "16px",
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        ...cardStyles.posted,
        "&:hover": {
          transform: "translateY(-6px)",
          ...cardStyles.posted["&:hover"],
        },
        "&:active": {
          transform: "translateY(-2px)",
        },
        "@media (hover: none)": {
          "&:hover": {
            transform: "none",
          },
          "&:active": {
            transform: "scale(0.98)",
          },
        },
      }}
    >
      <CardActionArea
        onClick={() => onClick(post)}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          touchAction: "manipulation",
        }}
      >
        {/* Banner Image */}
        {post.banner_image && (
          <MDBox
            component="img"
            src={post.banner_image}
            alt={post.title}
            sx={{
              width: "100%",
              height: { xs: 160, sm: 180, md: 200 },
              objectFit: "cover",
              transition: "transform 0.3s ease",
              ".MuiCard-root:hover &": {
                transform: "scale(1.05)",
              },
            }}
          />
        )}

        <CardContent sx={{ flexGrow: 1, width: "100%", p: { xs: 2, sm: 2.5, md: 3 } }}>
          {/* Status Badge and Date */}
          <MDBox
            mb={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            {/* Published Badge */}
            <MDBox
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: "20px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                color: "#4CAF50",
              }}
            >
              <Icon sx={{ fontSize: "0.9rem !important" }}>check_circle</Icon>
              <MDTypography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "inherit",
                }}
              >
                Published
              </MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
              {formatDate(post.published_at || post.date || post.created_at)}
            </MDTypography>
          </MDBox>

          {/* Title */}
          <MDBox mb={1.5}>
            <MDTypography
              variant="h5"
              fontWeight="bold"
              gutterBottom
              sx={{
                fontSize: { xs: "1.05rem", sm: "1.15rem", md: "1.25rem" },
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: "#1a1a2e",
              }}
            >
              {post.title}
            </MDTypography>
          </MDBox>

          {/* Abstract */}
          <MDBox mb={2}>
            <MDTypography
              variant="body2"
              color="text"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.65,
                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                color: "#4a4a68",
              }}
            >
              {post.abstract}
            </MDTypography>
          </MDBox>

          {/* Social Media Links - Platform Icons */}
          {hasSocialLinks && (
            <MDBox mt="auto" pt={2} borderTop="1px solid" borderColor="rgba(0,0,0,0.06)">
              <MDBox display="flex" alignItems="center" justifyContent="space-between">
                <MDBox display="flex" gap={0.75}>
                  {post.blog_url && (
                    <Tooltip title="View Blog Post" arrow>
                      <MDBox
                        component="a"
                        href={post.blog_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          backgroundColor: platformConfig.blog.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.1)",
                            backgroundColor: "#E5E7EB",
                          },
                        }}
                      >
                        <Icon
                          sx={{ fontSize: "1rem !important", color: platformConfig.blog.color }}
                        >
                          {platformConfig.blog.icon}
                        </Icon>
                      </MDBox>
                    </Tooltip>
                  )}
                  {post.linkedin_post_url && (
                    <Tooltip title="View on LinkedIn" arrow>
                      <MDBox
                        component="a"
                        href={post.linkedin_post_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          backgroundColor: platformConfig.linkedin.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.1)",
                            backgroundColor: "#D4EDFC",
                          },
                        }}
                      >
                        <Icon
                          sx={{ fontSize: "1rem !important", color: platformConfig.linkedin.color }}
                        >
                          {platformConfig.linkedin.icon}
                        </Icon>
                      </MDBox>
                    </Tooltip>
                  )}
                  {post.x_post_url && (
                    <Tooltip title="View on X" arrow>
                      <MDBox
                        component="a"
                        href={post.x_post_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          backgroundColor: platformConfig.x.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.1)",
                            backgroundColor: "#E8E8E8",
                          },
                        }}
                      >
                        <MDTypography
                          sx={{
                            fontWeight: 800,
                            fontSize: "0.85rem",
                            color: platformConfig.x.color,
                          }}
                        >
                          𝕏
                        </MDTypography>
                      </MDBox>
                    </Tooltip>
                  )}
                  {post.threads_post_url && (
                    <Tooltip title="View on Threads" arrow>
                      <MDBox
                        component="a"
                        href={post.threads_post_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          backgroundColor: platformConfig.threads.bg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            transform: "scale(1.1)",
                            backgroundColor: "#E8E8E8",
                          },
                        }}
                      >
                        <Icon
                          sx={{ fontSize: "1rem !important", color: platformConfig.threads.color }}
                        >
                          {platformConfig.threads.icon}
                        </Icon>
                      </MDBox>
                    </Tooltip>
                  )}
                </MDBox>
                <MDTypography variant="caption" sx={{ color: "#9CA3AF", fontSize: "0.7rem" }}>
                  {platformCount} platform{platformCount !== 1 ? "s" : ""}
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

PostedCard.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    abstract: PropTypes.string,
    banner_image: PropTypes.string,
    created_at: PropTypes.string,
    date: PropTypes.string,
    published_at: PropTypes.string,
    sourceChannel: PropTypes.string,
    blog_url: PropTypes.string,
    linkedin_post_url: PropTypes.string,
    x_post_url: PropTypes.string,
    threads_post_url: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

export default PostedCard;
